import React from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

import styles from 'components/Session/styles.module.scss';

const qrCodeErrorCallback = (err: any) => {
	console.log(err);
};

// Получает сообщение об ошибке на основе типа ошибки камеры
const getCameraErrorMessage = (err: any): string => {
	if (typeof err !== 'string') {
		return 'Неизвестная ошибка. Попробуйте перезагрузить страницу.';
	}

	if (err.includes('NotFoundError')) {
		return 'Устройство не найдено. Проверьте подключение камеры.';
	}
	if (err.includes('NotAllowedError')) {
		return 'Запрещен доступ к камере, просьба выдать доступ браузеру и повторить действие в системе';
	}
	if (err.includes('NotReadableError')) {
		return 'Камера занята другим приложением. Закройте другие приложения и попробуйте снова.';
	}
	if (err.includes('OverconstrainedError')) {
		return 'Камера не соответствует необходимым параметрам. Попробуйте другой тип камеры.';
	}
	if (err.includes('SecurityError')) {
		return 'Безопасность браузера не позволяет доступ к камере. Проверьте настройки безопасности.';
	}
	if (err.includes('AbortError')) {
		return 'Ошибка при запуске камеры. Попробуйте снова.';
	}

	return 'Неизвестная ошибка. Попробуйте перезагрузить страницу.';
};

// Создает callback функцию для успешного сканирования QR-кода
const createQrCodeSuccessCallback = (mode: string | undefined, successCallback: (code: string) => void) => {
	return (decodedText: string) => {
		const title =
			mode === 'open'
				? 'Зафиксировать время прихода в универмаг (не является временем начала смены)'
				: 'Зафиксировать время ухода из универмага (не является временем завершения смены)';

		if (window.confirm(title)) {
			successCallback(decodedText);
		}
	};
};

export interface IConfig {
	fps: number;
	qrbox: { width: number; height: number };
	formatsToSupport: Html5QrcodeSupportedFormats[];
}

export interface IScaner {
	successCallback: (code: string) => void;
	mode: string | undefined;
	config: IConfig;
	facingModeUser: boolean;
}

export default function Scaner(props: IScaner) {
	const [scanner, setScanner] = React.useState<Html5Qrcode | null>(null);
	const [isScanning, setIsScanning] = React.useState(false);

	React.useEffect(() => {
		const createScanner = async () => {
			try {
				const qrCodeScanner = new Html5Qrcode('reader');
				setScanner(qrCodeScanner);
			} catch (err) {
				console.error(err);
			}
		};

		createScanner();
	}, []);

	React.useEffect(() => {
		let isMounted = true;
		const startScanner = async () => {
			if (!scanner || isScanning || !isMounted) {
				return;
			}

			const qrCodeSuccessCallback = createQrCodeSuccessCallback(props.mode, props.successCallback);

			try {
				const qrboxWidth = Math.max(props.config.qrbox.width, 250);
				const qrboxHeight = Math.max(props.config.qrbox.height, 250);
				props.config.qrbox.width = qrboxWidth;
				props.config.qrbox.height = qrboxHeight;

				await scanner.start(
					{ facingMode: props.facingModeUser ? 'user' : 'environment' },
					props.config,
					qrCodeSuccessCallback,
					qrCodeErrorCallback,
				);

				if (isMounted) {
					setIsScanning(true);
				}
			} catch (err) {
				const errorMessage = getCameraErrorMessage(err);
				alert(errorMessage);
				console.error(err);
			}
		};

		startScanner();

		return () => {
			isMounted = false;
			const stopScanner = async () => {
				try {
					if (scanner && isScanning) {
						await scanner.stop();
						setIsScanning(false);
						console.log('Scanner stopped successfully');
					}
				} catch (err) {
					console.log('Error stopping scanner:', err);
				}
			};

			stopScanner();
		};
	}, [scanner, props.facingModeUser, isScanning]);

	return (
		<div>
			<div id="reader" className={styles.reader} />
		</div>
	);
}
